import {
  createContext,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useIntl } from 'react-intl';
import { SidepanelContext } from '@designsystem/stories/04-organisms/SidepanelContext';
import dateFormat from '@fhnw/components/DateFormat/dateFormat';

/** Stable id used to open/close the single registration sidepanel. */
export const PANEL_ID = 'occasion-registration';

/** Owning-event summary (carries at least a title and an @id). */
export interface RegistrationEvent {
  title?: string;
  '@id'?: string;
  [key: string]: unknown;
}

/** The occasion (serialized fields from the backend). */
export interface RegistrationOccasion {
  UID?: string;
  occasion_title?: string;
  occasion_start?: string | null;
  occasion_end?: string | null;
  open_end?: boolean | null;
  whole_day?: boolean | null;
  place_short?: string;
  cms_application?: boolean;
  cms_form_path?: string;
  crm_application?: boolean;
  crm_embed_code?: string;
  '@id'?: string;
  [key: string]: unknown;
}

/** Payload describing which occasion to register for. */
export interface OccasionRegistrationPayload {
  uid?: string;
  event?: RegistrationEvent;
  occasion?: RegistrationOccasion;
}

/** Display values keyed by the form's hidden FIELD_IDS (consumed by B6). */
export interface OccasionRegistrationHidden {
  eventname?: string;
  durchfuehrung?: string;
  datum: string;
  durchfuehrungsort?: string;
}

export interface OccasionRegistrationContextValue {
  openForOccasion: (payload: OccasionRegistrationPayload) => void;
  close: () => void;
  payload: OccasionRegistrationPayload | null;
  occasionUid: string | undefined;
  hidden: OccasionRegistrationHidden;
}

const NOOP_HIDDEN: OccasionRegistrationHidden = { datum: '' };

/**
 * Safe no-op default so `useOccasionRegistration()` can be called outside the
 * provider (e.g. cards on pages that never open the panel) without crashing.
 */
const DEFAULT_VALUE: OccasionRegistrationContextValue = {
  openForOccasion: () => {},
  close: () => {},
  payload: null,
  occasionUid: undefined,
  hidden: NOOP_HIDDEN,
};

const OccasionRegistrationContext =
  createContext<OccasionRegistrationContextValue>(DEFAULT_VALUE);

export const OccasionRegistrationProvider = ({
  children,
}: PropsWithChildren) => {
  const intl = useIntl();
  const [, doOpen, doClose] = useContext(SidepanelContext)!;
  const [payload, setPayload] = useState<OccasionRegistrationPayload | null>(
    null,
  );

  const value = useMemo<OccasionRegistrationContextValue>(() => {
    const event = payload?.event;
    const occasion = payload?.occasion;

    const datum = occasion?.occasion_start
      ? dateFormat({
          start: new Date(occasion.occasion_start),
          end: occasion?.occasion_end
            ? new Date(occasion.occasion_end)
            : undefined,
          // Render the date "analog Durchführungscard" (#1663): the card passes
          // these flags to the same helper, so the panel must too or whole-day /
          // open-end occasions would format differently than the card.
          openEnd: occasion?.open_end ?? undefined,
          wholeDay: occasion?.whole_day ?? undefined,
          locale: intl.locale,
        })
      : '';

    return {
      openForOccasion: (next: OccasionRegistrationPayload) => {
        setPayload(next);
        doOpen(PANEL_ID);
      },
      close: () => {
        doClose();
        setPayload(null);
      },
      payload,
      occasionUid: payload?.uid ?? occasion?.UID,
      hidden: {
        eventname: event?.title,
        durchfuehrung: occasion?.occasion_title,
        datum,
        durchfuehrungsort: occasion?.place_short,
      },
    };
  }, [payload, doOpen, doClose, intl.locale]);

  return (
    <OccasionRegistrationContext.Provider value={value}>
      {children}
    </OccasionRegistrationContext.Provider>
  );
};

/**
 * Read the registration context. Returns safe no-op defaults when used outside
 * an `OccasionRegistrationProvider`.
 */
export const useOccasionRegistration = (): OccasionRegistrationContextValue =>
  useContext(OccasionRegistrationContext);
